"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect, useRef, useState } from "react";
import { CheckCircle } from "lucide-react";
const categories = [{
  id: 1,
  title: "Transaction Records",
  description: "We collect comprehensive transaction data including dates, amounts, payment methods, and merchant information to ensure accurate financial tracking."
}, {
  id: 2,
  title: "Account Information",
  description: "Basic account details such as names, email addresses, and business identifiers are gathered to maintain secure access and personalized service."
}, {
  id: 3,
  title: "Financial Statements",
  description: "Periodic financial reports including balance sheets, income statements, and cash flow data are processed for analysis and compliance."
}, {
  id: 4,
  title: "Tax Documentation",
  description: "Tax-related information such as receipts, invoices, and filing documents are collected to support accurate tax preparation and reporting."
}, {
  id: 5,
  title: "Usage Analytics",
  description: "We track platform usage patterns, feature interactions, and session data to improve our services and optimize user experience."
}];
function CategoryItem({
  category,
  index
}: {
  category: typeof categories[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  return <div ref={ref} data-index={index} className={`group flex items-start gap-4 py-6 border-l-[3px] border-transparent pl-6 transition-all duration-300 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`} style={{
    transitionDelay: `${index * 100}ms`
  }}>
      <div className="relative flex-shrink-0 mt-1">
        <CheckCircle className={`w-6 h-6 transition-all duration-300 ${isVisible ? "text-primary scale-100 opacity-100" : "text-foreground scale-75 opacity-0"}`} />
        <div className={`absolute inset-0 rounded-full transition-all duration-500 ${isVisible ? "bg-primary/20 blur-md scale-150 opacity-100" : "opacity-0 scale-50"}`} />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-lg font-semibold mb-2 transition-colors duration-300 !text-[hsl(210,17%,98%)]">
          {category.title}
        </h3>
        <p className="text-sm leading-relaxed transition-all duration-500 opacity-100 !text-[hsl(210,17%,98%)]" style={{
        transitionDelay: `${index * 100 + 150}ms`
      }}>
          {category.description}
        </p>
      </div>
    </div>;
}
export default function DataCollectionDetails() {
  const parallaxRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current) {
        const rect = parallaxRef.current.getBoundingClientRect();
        const scrollProgress = Math.max(0, Math.min(1, (window.innerHeight - rect.top) / (window.innerHeight + rect.height)));
        setScrollY(scrollProgress * 40);
      }
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="data-collection-details" className="relative py-24 md:py-32 bg-dark-section overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {}
          <div className="space-y-2">
            <h2 className="text-4xl font-bold mb-8 !text-[hsl(0,0%,100%)] md:text-5xl">
              Data Collection Details
            </h2>
            <p className="text-dark-foreground/70 mb-12 text-lg">
              Transparency in how we collect and process your financial
              information
            </p>
            <div className="space-y-1">
              {categories.map((category, index) => <CategoryItem key={category.id} category={category} index={index} />)}
            </div>
          </div>

          {}
          <div ref={parallaxRef} className="relative h-[600px] lg:h-[700px] rounded-lg overflow-hidden">
            <div className="absolute inset-0 transition-transform duration-100 ease-out" style={{
            transform: `translateY(${scrollY}px)`,
            willChange: "transform"
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1542563/img/financial_data_abstract_visualization.png" alt="Abstract financial data visualization" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-dark-section/80 via-dark-section/20 to-transparent" />
            </div>

            {}
            <div className="absolute top-8 right-8 w-24 h-24 rounded-full bg-primary/10 blur-2xl" />
            <div className="absolute bottom-12 left-12 w-32 h-32 rounded-full bg-accent/10 blur-3xl" />
          </div>
        </div>
      </div>
    </section>;
}